/**
 * Dynamic form that renders one input per field declared in the
 * IntegrationDefinition. Knows nothing about specific providers —
 * works for any catalog entry, current or future (NetSuite, etc).
 *
 * Rendered inside a Modal — the integration's displayName + description
 * are surfaced by the page through the Modal title; this form supplies
 * only the field inputs + actions.
 */
import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { Banner, Button, Input } from '@servicecompressionllc/sc-design-system';
import type { IntegrationDefinition, IntegrationField } from '../../lib/types.js';
import { useIntegrationFormLogic, type IntegrationFormMode } from './useIntegrationFormLogic.js';

interface IntegrationFormProps {
  tenantId: string;
  definition: IntegrationDefinition;
  mode: IntegrationFormMode;
  /**
   * Stored non-secret values for Edit mode (from
   * `TenantIntegrationSummary.config`). Lets the form prefill what's
   * currently configured so the admin can see + edit it. Empty / omitted
   * in Create mode.
   */
  prefill?: Record<string, string>;
  /**
   * Edit mode: the row being updated. PUT addresses a configured row by id — a
   * catalog key can have several instances, so it can't name one.
   */
  integrationId?: string;
  /**
   * Whether this kind of integration can have its own name, so a tenant can hold
   * more than one of it. Set for connection-category integrations.
   */
  allowNamedInstance?: boolean;
  /** The instance's current name, so Edit can show and change it. */
  currentName?: string;
  onCancel: () => void;
  onSuccess: () => void;
}

const inputTypeFor = (field: IntegrationField): 'password' | 'text' => {
  if (field.kind === 'secret') return 'password';
  return 'text';
};

const handleFileUpload = async (
  e: ChangeEvent<HTMLInputElement>,
  fieldName: string,
  setField: (name: string, value: string) => void,
): Promise<void> => {
  const file = e.target.files?.[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (event) => {
    const content = event.target?.result as string;
    setField(fieldName, content);
  };
  reader.readAsText(file);
};

/** A slug the server will accept: lowercase alphanumerics and hyphens. */
const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

export const IntegrationForm = ({
  tenantId,
  definition,
  mode,
  prefill,
  integrationId,
  allowNamedInstance = false,
  currentName,
  onCancel,
  onSuccess,
}: IntegrationFormProps) => {
  const [name, setName] = useState(currentName ?? '');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  // The NAME is editable whenever an instance can have one — it's a label. The
  // SLUG is create-only: it names the row's Key Vault secret and tags records the
  // source returns, so changing it would orphan the one and rewrite the other.
  const showName = allowNamedInstance;
  const showSlug = allowNamedInstance && mode === 'create';

  const { fields, setField, isSubmitting, error, submit } = useIntegrationFormLogic(
    tenantId,
    definition,
    mode,
    onSuccess,
    prefill,
    integrationId,
    showSlug ? { name, slug } : undefined,
    showName && mode === 'edit' ? name : undefined,
  );

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    void submit();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-surface-elevated rounded-lg border border-outline p-5 space-y-4"
    >
      {mode === 'edit' && (
        <p className="text-ink-subtle text-xs">
          Leave a field blank to keep its current value. Only fields you fill in will be updated.
        </p>
      )}

      {error && (
        <Banner variant="danger">
          <Banner.Body>
            <Banner.Title>{error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {showName && (
        <div className="space-y-1">
          <label htmlFor="instance-name" className="text-ink block text-sm font-medium">
            Name
          </label>
          <Input
            id="instance-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              // Track the name until the slug is edited by hand, so the common
              // case needs no thought and the rare case stays possible.
              if (!slugEdited) setSlug(slugify(e.target.value));
            }}
            placeholder="Ricky Curator Docs"
          />
          <p className="text-ink-subtle text-xs">What you'll recognise it by on this page.</p>
        </div>
      )}

      {showSlug && (
        <div className="space-y-1">
          <label htmlFor="instance-slug" className="text-ink block text-sm font-medium">
            Slug
          </label>
          <Input
            id="instance-slug"
            value={slug}
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(slugify(e.target.value));
            }}
            placeholder="curator"
          />
          <p className="text-ink-subtle text-xs">
            Fixed once saved — it tags records this source returns and names its stored secret.
          </p>
        </div>
      )}

      {definition.fields.map((field) => {
        const isSecret = field.kind === 'secret';
        const required = mode === 'create' && isSecret;
        const descriptionId = `field-${field.name}-description`;

        // Check conditional visibility
        if (field.dependsOn && field.visibleWhen) {
          const dependentValue = fields[field.dependsOn];
          if (dependentValue !== field.visibleWhen) {
            return null; // Hide this field
          }
        }

        // A short closed choice renders as cards: the label says what, the line
        // under it says how, so an admin picks without a glossary.
        if (field.inputType === 'radio' && field.options) {
          const current = fields[field.name] ?? field.defaultValue ?? '';
          return (
            <fieldset key={field.name} className="min-w-0">
              <legend className="block text-sm font-medium text-ink">{field.label}</legend>
              <p id={descriptionId} className="text-ink-muted text-xs mt-1 mb-2">
                {field.description}
              </p>
              <div
                className="grid gap-2.5 sm:grid-cols-2"
                role="radiogroup"
                aria-describedby={descriptionId}
              >
                {field.options.map((opt) => {
                  const checked = current === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer items-start gap-2.5 rounded-md border p-3 ${
                        checked
                          ? 'border-primary bg-surface-elevated'
                          : 'border-outline-strong bg-surface hover:bg-surface-hover'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`field-${field.name}`}
                        value={opt.value}
                        checked={checked}
                        disabled={isSubmitting}
                        onChange={() => setField(field.name, opt.value)}
                        className="accent-primary mt-0.5"
                      />
                      <span className="flex flex-col gap-0.5">
                        <span className="text-ink text-sm font-semibold">{opt.label}</span>
                        {opt.description && (
                          <span className="text-ink-muted text-[13px] leading-snug">
                            {opt.description}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          );
        }

        // Render select input
        if (field.inputType === 'select' && field.options) {
          return (
            <div key={field.name}>
              <label htmlFor={`field-${field.name}`} className="block text-sm font-medium text-ink">
                {field.label}
              </label>
              <p id={descriptionId} className="text-ink-muted text-xs mt-1 mb-2">
                {field.description}
              </p>
              <select
                id={`field-${field.name}`}
                value={fields[field.name] ?? ''}
                onChange={(e) => setField(field.name, e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-outline rounded-md bg-surface text-ink text-sm disabled:opacity-50"
                aria-describedby={descriptionId}
              >
                <option value="">-- Select an option --</option>
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          );
        }

        // Render file upload for private keys
        if (field.name === 'privateKey') {
          const hasContent = (fields[field.name] ?? '').length > 0;
          return (
            <div key={field.name}>
              <label htmlFor={`field-${field.name}`} className="block text-sm font-medium text-ink">
                {field.label}
              </label>
              <p id={descriptionId} className="text-ink-muted text-xs mt-1 mb-2">
                {field.description}
              </p>
              <div className="flex items-center gap-3">
                <input
                  id={`field-${field.name}`}
                  type="file"
                  accept=".pem,.key"
                  onChange={(e) => void handleFileUpload(e, field.name, setField)}
                  disabled={isSubmitting}
                  className="block w-full text-sm text-ink file:mr-4 file:px-4 file:py-2 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-outline file:text-ink hover:file:bg-outline/80 disabled:opacity-50"
                  aria-describedby={descriptionId}
                />
                {hasContent && <span className="text-xs text-ink-muted">✓ Loaded</span>}
              </div>
            </div>
          );
        }

        // Render text/password input
        return (
          <div key={field.name}>
            <label htmlFor={`field-${field.name}`} className="block text-sm font-medium text-ink">
              {field.label}
            </label>
            <p id={descriptionId} className="text-ink-muted text-xs mt-1 mb-2">
              {field.description}
            </p>
            <Input
              id={`field-${field.name}`}
              type={inputTypeFor(field)}
              value={fields[field.name] ?? ''}
              onChange={(e) => setField(field.name, e.target.value)}
              disabled={isSubmitting}
              required={required}
              placeholder={
                mode === 'edit' && isSecret ? '••••••••  (leave blank to keep current)' : undefined
              }
              autoComplete={isSecret ? 'new-password' : 'off'}
              aria-describedby={descriptionId}
            />
          </div>
        );
      })}

      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : mode === 'create' ? 'Connect integration' : 'Save changes'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
